import { Link } from "react-router-dom";
import AppleSection from "@/components/AppleSection";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";

interface Service {
  id: string;
  emoji: string;
  title: string;
  description: string;
  
  agentName?: string;
  agentRole?: string;
}

const services: Service[] = [
  {
    id: "automatizacion",
    emoji: "⚡",
    title: "Automatización de Procesos",
    description: "Emails, facturas y flujos que trabajan solos. Recupera el 65% de tu tiempo.",
    agentName: "NEXO",
    agentRole: "Agente de procesos",
  },
  {
    id: "atencion-cliente",
    emoji: "🎤",
    title: "Atención al Cliente 24/7",
    description: "Agentes de voz y WhatsApp que nunca duermen. Cero llamadas perdidas.",
    agentName: "Marta",
    agentRole: "Agente de atención",
  },
  {
    id: "contenido-ia",
    emoji: "🎨",
    title: "Contenido Profesional con IA",
    description: "Vídeos, presentaciones y diseños automatizados. 10× más contenido.",
    agentName: "PIXEL",
    agentRole: "Agente creativo",
  },
  {
    id: "presencia-digital",
    emoji: "🌐",
    title: "Presencia Digital Express",
    description: "Webs y landing pages creadas con IA en días, no meses.",
  },
  {
    id: "gestion-administrativa",
    emoji: "📋",
    title: "Gestión Administrativa Digital",
    description: "Bases de conocimiento y actas automáticas. Menos papeleo, más control.",
  },
  {
    id: "diagnostico-ia",
    emoji: "💡",
    title: "Diagnóstico de Madurez IA",
    description: "Auditoría y hoja de ruta personalizada. Saber por dónde empezar.",
  },
];

export default function Servicios() {
  useSEO({
    title: "Servicios de IA para Pymes | Globaltis IA",
    description: "Automatización, atención al cliente 24/7, contenido con IA, presencia digital, gestión administrativa y diagnóstico de madurez IA. Soluciones reales para pymes.",
    canonical: "https://globaltisia.lovable.app/servicios",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Servicios de IA para Pymes",
      itemListElement: services.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.title,
        url: `https://globaltisia.lovable.app/servicios/${s.id}`
      }))
    }
  });

  return (
    <div className="min-h-screen">
      <AppleSection background="black" padding="large">
        <div className="text-center space-y-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light text-white dark:text-black">
            Nuestros Servicios
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light text-gray-400 dark:text-gray-600 max-w-4xl mx-auto">
            Soluciones de IA diseñadas para que tu pyme crezca sin complicaciones.
          </p>
        </div>
      </AppleSection>

      <AppleSection background="white" padding="large">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/servicios/${service.id}`}
                className="group space-y-4 p-5 sm:p-8 rounded-2xl hover:bg-secondary transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border border-border/30"
              >
                {service.agentName && (
                  <div className="space-y-0.5">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      {service.agentName}
                    </p>
                    {service.agentRole && (
                      <p className="text-sm text-muted-foreground">{service.agentRole}</p>
                    )}
                  </div>
                )}
                <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-foreground group-hover:text-primary transition-colors">
                  {service.title}
                  <span className="ml-3 text-2xl group-hover:translate-x-2 inline-block transition-transform">→</span>
                </h2>
                <p className="text-lg text-muted-foreground">
                  {service.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </AppleSection>

      <AppleSection background="black" padding="normal">
        <div className="text-center space-y-8">
          <p className="text-2xl md:text-3xl font-light text-white dark:text-black">
            ¿No sabes por dónde empezar?
          </p>
          <p className="text-xl text-gray-400 dark:text-gray-600">
            Te ayudamos a encontrar el servicio que más impacto tendrá en tu negocio.
          </p>
          <Link
            to="/contacto"
            className="inline-block px-8 py-4 bg-white dark:bg-black text-black dark:text-white text-lg font-semibold rounded-full hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
          >
            Hablar con un experto
          </Link>
        </div>
      </AppleSection>

      <Footer />
    </div>
  );
}